/**
 * Twinkle Product Form — <product-form> web component
 * @module product/form
 * Helpers: product-form-helpers.js (TwinkleProductForm)
 * Cart integration: cart-* (TwinkleCart)
 */
if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.form.querySelector('[name=id]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.addOnToggle = this.querySelectorAll('input.addon-product-toggle');
      this.submitButton = this.querySelector('[type="submit"]');

      if (document.querySelector('cart-drawer')) {
        if (this.submitButton) {
          this.submitButton.setAttribute('aria-haspopup', 'dialog');
        }
      }

      if (this.addOnToggle) {
        this.addOnToggle.forEach(addOnToggle => {
          addOnToggle.addEventListener('change', e => {
            const target = e.currentTarget;
            if (!target.checked) {
              const wrapper = target.closest('.additional-props');
              const inputs = wrapper.querySelectorAll('input[type="text"]');
              inputs.forEach(input => {
                if (input.classList.contains('input-number')) {
                  input.value = 0;
                  const product_handle = input.dataset.handle;
                  const hiddenInput = document.querySelector(`input[name="properties[Accessories][${product_handle}]"]`);
                  if (hiddenInput) {
                    hiddenInput.value = 0;
                  }
                  input.dispatchEvent(new Event('change', { bubbles: true }));
                } else {
                  input.value = "";
                  const name = input.getAttribute('name');
                  const hiddenInput = document.querySelector(`input[type="hidden"][name="${name}"]`);
                  if (hiddenInput) {
                    hiddenInput.value = "";
                  }
                }
                input.dispatchEvent(new Event('input', { bubbles: true }));
              });
            }
          });
        });
      }

      this.propProducts = this.querySelectorAll('input[name^="properties[Props]"]');
    }

    connectedCallback() {
      this.syncSubmitButton();
      const messageField = document.querySelector('input#message-on-the-cake');
      if (messageField) {
        messageField.addEventListener('input', e => {
          const value = e.target.value;
          if (value != '') {
            const errorEl = e.target.parentElement.querySelector('span.error');
            if (errorEl) {
              errorEl.remove();
            }
          }
        });
      }
    }

    syncSubmitButton() {
      this.submitButton = this.querySelector('[type="submit"]');
      return this.submitButton;
    }

    getSubmitSpinner() {
      return this.submitButton?.querySelector('.loading-overlay__spinner') || null;
    }

    setSubmitLoading(isLoading) {
      this.syncSubmitButton();
      if (!this.submitButton) return;

      const spinner = this.getSubmitSpinner();
      if (isLoading) {
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        spinner?.classList.remove('hidden');
        return;
      }

      this.submitButton.classList.remove('loading');
      if (!this.error) this.submitButton.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      this._lastValidationPassed = false;
      this.syncSubmitButton();

      if (!this.submitButton || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      const backdropMsg = this.querySelector('input[name="properties[Message on the backdrop]"]') ||
        this.querySelector('customization-product .line-item-property__field input[name="properties[Message on the cake]"]') ||
        this.querySelector('customization-product .line-item-property__field input[name="properties[Name on the mosque]"]') ||
        this.querySelector('customization-product .line-item-property__field input[name="properties[Message on backdrop]"]');

      const msgOfAddon = this.querySelector('customization-product .line-item-property__field input[name="properties[Message on Giveaway Boxes]"]');
      const addOnToggle = this.querySelector('customization-product input.addon-product-toggle');

      if (backdropMsg && backdropMsg?.value.length == 0) {
        const formField = this.querySelector('input#message-on-the-cake');
        const fieldWrapper = formField?.closest('.line-item-property__field');
        const errorEl = fieldWrapper?.querySelector('span.error');
        if (errorEl) {
          fieldWrapper.removeChild(errorEl);
        }
        const messageEl = document.createElement('span');
        let message = backdropMsg.dataset.errorMessage;

        if (!backdropMsg.dataset.errorMessage) {
          message = window.errorStrings.errorLabel;
        }

        messageEl.classList.add('error');
        messageEl.innerText = message;
        if (fieldWrapper) {
          fieldWrapper.appendChild(messageEl);
          TwinkleProductForm.smoothScrollTo(fieldWrapper, 800);
          return;
        }
      }

      if (addOnToggle && addOnToggle?.checked) {
        if (msgOfAddon && msgOfAddon?.value.length == 0) {
          const formField = document.querySelector('input#message-on-the-addon');
          const fieldWrapper = formField?.closest('.line-item-property__field');

          const errorEl = fieldWrapper?.querySelector('span.error');
          if (errorEl) {
            fieldWrapper.removeChild(errorEl);
          }
          const messageEl = document.createElement('span');

          let message = msgOfAddon.dataset.errorMessage;
          if (!msgOfAddon.dataset.errorMessage) {
            message = window.errorStrings.errorLabel;
          }
          messageEl.classList.add('error');
          messageEl.innerText = message;
          if (fieldWrapper) {
            fieldWrapper.appendChild(messageEl);
            TwinkleProductForm.smoothScrollTo(fieldWrapper, 800);
          }
          return;
        }
      }

      const genderSelector = this.querySelector('[name="properties[Choose Gender]"]');
      if (genderSelector) {
        if (!genderSelector.value || String(genderSelector.value).trim().length === 0) {
          const genderErr = this.querySelector('#genderErrorMsg');
          genderErr?.classList.remove('hidden');
          return;
        }
        this.querySelector('#genderErrorMsg')?.classList.add('hidden');
      }

      const additionalInputFields = this.querySelectorAll('input.additional_input_field');
      if (additionalInputFields.length) {
        for (const input of additionalInputFields) {
          const isRequired = input.dataset.isRequired === "true";
          const isFile = input.type === 'file';
          const isEmpty = isFile
            ? !(input.files && input.files.length)
            : input.value.trim() === "";

          const name = input.dataset.name;
          // Quoted, because the name comes from an admin-authored metaobject
          // field: unquoted, `[data-for=field-for-Message on backdrop]` is not
          // a valid selector and querySelector throws, which kills add-to-cart
          // silently. And the lookup can legitimately miss -- the error branch
          // below already guards for that, this line did not.
          const associated_section = name
            ? document.querySelector(`[data-for="field-for-${name}"]`)
            : null;
          const errorEl = associated_section?.querySelector('.error');
          if (isRequired && isEmpty) {
            console.warn(`Required field "${input.name || input.id}" is empty.`);
            if (errorEl) {
              errorEl.classList.remove('hidden');
              TwinkleProductForm.smoothScrollTo(associated_section, 800);
            }
            else {
              const messageEl = document.createElement('span');

              let message = input.dataset.errorMessage;
              if (!input.dataset.errorMessage) {
                message = window.errorStrings.errorLabel;
              }
              messageEl.classList.add('error');
              messageEl.innerText = message;
              if (associated_section) {
                associated_section.appendChild(messageEl);
                TwinkleProductForm.smoothScrollTo(associated_section, 800);
              }
            }
            return; // Stop the function here
          }

          if (errorEl) { errorEl.classList.add('hidden'); }
        }
      }

      this._lastValidationPassed = true;
      if (this.dataset.validateOnly === 'true') {
        this.dataset.validateOnly = '';
        return;
      }

      this.setSubmitLoading(true);

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      if (this.id === 'packageProduct' || this.id === 'hasPropProduct') {
        TwinkleProductForm.syncPdpAddonInputsFromUI(this);
      }

      const formData = new FormData();
      let formDataMain = new FormData(this.form);
      formDataMain = TwinkleProductForm.expandMultiFileProperties(formDataMain, this.form);

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        formDataMain.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formDataMain.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }

      config.body = formData;

      if (this.id == 'packageProduct') {

        for (const pair of formDataMain.entries()) {
          const key = pair[0];
          if (key.includes("properties")) {
            formData.append(`items[0][properties]${key.replace("properties", "")}`, pair[1]);
          }
        }

        const chooseGenderEl = this.querySelector('[name="properties[Choose Gender]"]');
        if (chooseGenderEl && String(chooseGenderEl.value).trim()) {
          formData.append('items[0][properties][Choose Gender]', chooseGenderEl.value.trim());
        }

        formData.append("items[0][id]", formDataMain.get("id"));
        formData.append("items[0][quantity]", formDataMain.get("quantity") ?? 1);

        Shopify.theme.cart.getState().then(async (res) => {
          let items = res.items;
          let product_id = formDataMain.get('product_id');
          let itemExists = false;
          let key = '';
          let line = 0;

          if (items.length > 0) {
            items.map((item, index) => {
              if (item.product_id == product_id) {
                itemExists = true;
                line = index;
                key = item.key;
              }
            });
          }

          let localStoragEditProduct = localStorage.getItem('edit-product');
          if (itemExists && formDataMain.get("id") == localStoragEditProduct) {
            const productHandle = this.dataset.handle;
            const properties = TwinkleProductForm.buildPackagePropertiesFromForm(formDataMain);
            const editKey = localStorage.getItem('edit-product-key') || key;
            const lineNumber = items.findIndex((item) => item.key === editKey) + 1;
            const changeBody = {
              line: lineNumber > 0 ? lineNumber : line + 1,
              quantity: parseInt(formDataMain.get('quantity') ?? 1, 10) || 1,
              properties,
            };

            if (this.cart) {
              changeBody.sections = this.cart.getSectionsToRender().map((section) => section.id);
              changeBody.sections_url = window.location.pathname;
            }

            fetch(`${routes.cart_change_url}`, {
              ...fetchConfig(),
              body: JSON.stringify(changeBody),
            })
              .then((response) => response.json())
              .then(async (res) => {
                if (res.status) {
                  this.handleErrorMessage(res.description);
                  return;
                }

                let finalRes = res;

                if (!TwinkleProductForm.packageHasAccessories(properties)) {
                  const addonUpdateRes = await TwinkleProductForm.removePackageAddonLines(
                    res.items,
                    productHandle,
                    this.cart
                  );
                  if (addonUpdateRes) finalRes = addonUpdateRes;
                }

                if (this.cart && finalRes.sections) {
                  TwinkleCart.renderDrawerResponse(finalRes);
                }

                TwinkleCart.dispatchCartUpdate(finalRes);
                this.closest('customization-product')?.syncAllQtyButtonStates?.();
              })
              .catch((err) => console.log("Error", err))
              .finally(() => {
                this.setSubmitLoading(false);
                if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
              });

            return false;
          }

          if (itemExists && formDataMain.get("id") != localStoragEditProduct && localStoragEditProduct != null) {
            const productHandle = this.dataset.handle;
            const updates = { [key]: 0 };

            items.forEach((item) => {
              if (item.properties?._package === productHandle) {
                updates[item.key] = 0;
              }
            });

            fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates }),
            })
              .then(() => {
                const localstorageindex = localStorage.getItem('edit-product-index');
                if (localstorageindex == this.dataset.index) {
                  document.querySelector('variant-radios')?.onVariantChange();
                }
                TwinkleProductForm.appendPackageAddonsToFormData(formData, formDataMain, productHandle);
                config.body = formData;
                this.addNewItemtoCart(config);
              })
              .catch((err) => console.log("Error", err));

            return false;
          }

          if (itemExists) {
            const productHandle = this.dataset.handle;
            const updates = {};
            updates[key] = 0;

            // Also find and remove all addons belonging to this package
            items.forEach(item => {
              if (item.properties && (item.properties._package === productHandle || item.properties._package === product_id)) {
                updates[item.key] = 0;
              }
            });

            fetch(window.Shopify.routes.root + 'cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            }).then(() => {
              TwinkleProductForm.appendPackageAddonsToFormData(formData, formDataMain, productHandle);
              config.body = formData;
              this.addNewItemtoCart(config);
            }).catch((err) => console.log("Error", err));

            return false;
          }

          if (!itemExists) {
            const productHandle = this.dataset.handle;
            TwinkleProductForm.appendPackageAddonsToFormData(formData, formDataMain, productHandle);
            document.querySelector('variant-radios')?.onVariantChange();
            config.body = formData;
            this.addNewItemtoCart(config);
            return false;
          }
        });
      } else if (this.id == 'hasPropProduct') {
        const productHandle = this.dataset.handle;
        const mainProductId = formDataMain.get('id');
        const mainProductQty = parseInt(formDataMain.get('quantity')) || 1;

        const res = await Shopify.theme.cart.getState();
        const updates = {};
        const updateButton = this.form.querySelector('button[name="update"]');

        if (updateButton) {
          const update_key = updateButton.dataset.itemKey;
          updates[update_key] = 0;
        }

        // Also find and remove all addons belonging to this package
        res.items.forEach(item => {
          if (item.properties && item.properties._package === productHandle) {
            updates[item.key] = 0;
          }
        });

        if (Object.keys(updates).length > 0) {
          await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          });
        }

        formData.append('items[0][id]', mainProductId);
        formData.append('items[0][quantity]', mainProductQty);

        let addonIndex = 1;
        for (const [key, value] of formDataMain.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch) {
            const propertyName = propertyMatch[1];
            if (propertyName.startsWith('_pdp_addon_') && value) {
              const [id, qtyRaw] = value.split(':');
              const qtyPerBundle = parseInt(qtyRaw) || 0;
              if (qtyPerBundle > 0) {
                const totalAddonQty = qtyPerBundle * mainProductQty;
                formData.append(`items[${addonIndex}][id]`, id);
                formData.append(`items[${addonIndex}][quantity]`, totalAddonQty);
                formData.append(`items[${addonIndex}][properties][_addonProduct]`, true);
                formData.append(`items[${addonIndex}][properties][_package]`, productHandle);
                addonIndex++;
              }
            } else if (propertyName !== 'Props' && value) {
              formData.append(`items[0][properties]${key.replace('properties', '')}`, value);
            }
          }
        }

        const propProducts = formDataMain.get('properties[Props]') ?? '';
        const propProductsArray = propProducts.split(',').filter(propProduct => propProduct !== '');
        propProductsArray.forEach((propId) => {
          formData.append(`items[${addonIndex}][id]`, propId);
          formData.append(`items[${addonIndex}][quantity]`, mainProductQty);
          formData.append(`items[${addonIndex}][properties][_addonProduct]`, true);
          formData.append(`items[${addonIndex}][properties][_package]`, productHandle);
          addonIndex++;
        });

        const sectionId = this.dataset.sectionId;
        if (sectionId) {
          const currentSections = formData.get('sections');
          if (currentSections && !currentSections.includes(sectionId)) {
            formData.set('sections', currentSections + ',' + sectionId);
          }
        }

        config.body = formData;
        this.addNewItemtoCart(config);

        return false;

        // end of add prop products to form data
      } else {
        config.body = formDataMain;
        Shopify.theme.cart.getState().then(async (res) => {
          let items = res.items;
          let product_id = formDataMain.get('product_id');
          let itemExists = false;
          let key = '';
          let line = 0;

          if (items.length > 0) {
            items.map((item, index) => {
              if (item.product_id == product_id) {
                itemExists = true;
                line = index;
                key = item.key;
              }
            });
          }

          if (itemExists) {
            Shopify.theme.cart.removeItem(key).then((res) => {
              const localstorageindex = localStorage.getItem('edit-product-index');
              if (localstorageindex == line) {
                document.querySelector('variant-radios')?.onVariantChange();
              }
            }).then(() => {
              this.addNewItemtoCart(config);
            }).catch((err) => console.log("Error", err));

            return false;
          }

          if (!itemExists) {
            this.addNewItemtoCart(config);
            return false;
          }

        });

      }
      setTimeout(function () {
        document.querySelector('variant-radios')?.onVariantChange();
      }, 1500);
    }

    syncChooseGenderCartAttribute() {
      const el = this.querySelector('[name="properties[Choose Gender]"]');
      if (!el || !String(el.value).trim()) return Promise.resolve();

      const root = window.Shopify?.routes?.root ?? '/';
      const base = root.endsWith('/') ? root : `${root}/`;
      const cartUrl = `${base}cart.js`;
      const updateUrl = `${base}cart/update.js`;

      return fetch(cartUrl, { credentials: 'same-origin' })
        .then((r) => r.json())
        .then((cart) => {
          const attrs =
            cart.attributes && typeof cart.attributes === 'object' && !Array.isArray(cart.attributes)
              ? { ...cart.attributes }
              : {};
          attrs['Choose Gender'] = el.value.trim();
          return fetch(updateUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ attributes: attrs })
          });
        })
        .then((r) => {
          if (!r.ok) throw r;
          return r.json();
        })
        .catch((err) => console.error('Choose Gender cart attribute:', err));
    }

    closeAlpineModalIfNeeded() {
      if (!this.closest('[data-modal-content]')) return;
      const alpineModal = window.Alpine?.store?.('alpineModal');
      if (alpineModal && typeof alpineModal.close === 'function') {
        alpineModal.close();
      }
    }

    addNewItemtoCart(config) {
      fetch(`${routes.cart_add_url}.js`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => {
                this.cart.renderContents(response);
                this.syncChooseGenderCartAttribute();
              });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
            const giftWrapPromise =
              response.items.length == 1
                ? fetch(window.Shopify.routes.root + 'cart/update.js', {
                    method: 'POST',
                    body: (() => {
                      const fd = new FormData();
                      fd.append('attributes[Gift wrap]', 'Yes');
                      return fd;
                    })()
                  }).then((r) => r.json())
                : Promise.resolve(null);

            giftWrapPromise
              .then(() => this.syncChooseGenderCartAttribute())
              .catch((e) => console.error(e));
          }

          this.closeAlpineModalIfNeeded();

          const sectionId = this.dataset.sectionId;
          const sectionHtml = sectionId && response.sections?.[sectionId];
          const atcContainer = document.querySelector('#nd-main-product-button');
          if (sectionHtml && atcContainer) {
            const productAtcButton = this.cart.getSectionInnerHTML(sectionHtml, '#nd-main-product-button');
            if (productAtcButton) {
              atcContainer.innerHTML = productAtcButton;
              this.syncSubmitButton();
            }
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.setSubmitLoading(false);
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
